import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Switch } from '@/components/form/baseInputs/Switch';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { EyeOffIcon } from '@/components/icons/EyeOffIcon';
import { Card } from '@/components/layout/Card/Card';
import { ModelMappingModelPurpose } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { MODEL_PURPOSE_DETAILS } from '../../modelPurposeDetails';
import { AzureByokIntegrationCard_OpenaiSdkConfigFragment } from '../graphql/AdminIntegrationsPage.generated';
import { AzureBYOKFormShape, useAzureBYOKForm } from './hooks/useAzureBYOKForm';

const MODEL_PURPOSES = Object.values(ModelMappingModelPurpose);

export interface AzureBYOKIntegrationCardProps {
  azureConfig: AzureByokIntegrationCard_OpenaiSdkConfigFragment | null;
  otherProviderEnabled: boolean;
  disabled?: boolean;
}

export function AzureBYOKIntegrationCard({
  azureConfig,
  otherProviderEnabled,
  disabled = false,
}: AzureBYOKIntegrationCardProps) {
  const [showApiKey, setShowApiKey] = useState(false);
  const { formMethods, loading, onSubmit, handleToggleEnabled } =
    useAzureBYOKForm({ azureConfig });
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = formMethods;
  const apiKeyValue = useWatch({ control, name: 'apiKey' });

  const isEnabled = azureConfig?.enabled ?? false;
  const hasConfig = !!azureConfig;

  // Toggle disabled when: other provider enabled OR no config exists
  const toggleDisabled =
    disabled || loading || (otherProviderEnabled && !isEnabled) || !hasConfig;

  // Form visible when: enabled OR neither provider is enabled (initial setup)
  const showForm = isEnabled || !otherProviderEnabled;

  return (
    <Stack>
      <Box textAlign="center">
        <img
          alt="Azure"
          src={getPublicImageUrl('/azureOpenAILogo.png')}
          height={40}
        />
      </Box>
      <Stack mt={1} spacing={2}>
        <Typography variant="body-s">
          Configure your Azure OpenAI endpoint for AI-related tasks on the
          Luminary Platform using your own Azure subscription.
        </Typography>
        <FormFieldsDisabledProvider isDisabled={disabled || loading}>
          <Card
            variant="filled"
            sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}
          >
            <Switch
              labelPosition="right"
              value={isEnabled}
              label="Use custom Azure OpenAI Credentials"
              disabled={toggleDisabled}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
            />
            {showForm && (
              <>
                <FormAwareTextInput<AzureBYOKFormShape>
                  control={control}
                  fieldName="baseURL"
                  label="Base URL"
                  required
                  validation={{
                    validUrl: (value) => {
                      if (!value) return true;
                      try {
                        new URL(value as string);
                        return true;
                      } catch {
                        return 'Please enter a valid URL';
                      }
                    },
                  }}
                />
                <FormAwareTextInput<AzureBYOKFormShape>
                  control={control}
                  fieldName="apiKey"
                  label="API Key"
                  type={showApiKey ? 'text' : 'password'}
                  required={!azureConfig}
                  helpText={
                    azureConfig
                      ? 'An API key is already configured. Enter a new key to update.'
                      : undefined
                  }
                  endAdornment={
                    <IconButton
                      variant="transparent"
                      size="sm"
                      icon={showApiKey ? EyeOffIcon : EyeIcon}
                      disabled={disabled || !apiKeyValue || loading}
                      onClick={() => setShowApiKey((prev) => !prev)}
                      ariaLabel="Toggle visibility"
                      sx={{ borderRadius: '50%', p: 1 }}
                    />
                  }
                />
                <Typography variant="title-s" sx={{ mt: 2 }}>
                  Model Mappings
                </Typography>
                {MODEL_PURPOSES.map((purpose) => (
                  <FormAwareTextInput<AzureBYOKFormShape>
                    key={purpose}
                    control={control}
                    required
                    fieldName={`models.${purpose}`}
                    label={MODEL_PURPOSE_DETAILS[purpose].label}
                    helpText={`Recommended model: ${MODEL_PURPOSE_DETAILS[purpose].recommendedModel}`}
                  />
                ))}
              </>
            )}
          </Card>
        </FormFieldsDisabledProvider>
      </Stack>
      {showForm && (
        <Box mt={2} mb={2} textAlign="center">
          <Button
            size="sm"
            variant="primary"
            fullWidth
            disabled={disabled || !isValid}
            loading={loading}
            onClick={handleSubmit(onSubmit)}
          >
            {isEnabled ? 'Update' : 'Enable'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
